import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import "./HowToPlay.scss";
import { swapPassPenalty } from "frontend/utils/constants.js";
import boardScreenshot from "frontend/assets/how-to-play/board-screenshot.png";

const HtpTile = ({ label, variant }) => (
  <div className={`htp-tile${variant ? ` htp-tile-${variant}` : ""}${label.length > 1 ? " htp-tile-word" : ""}`}>{label}</div>
);

const CardGoal = () => (
  <>
    <p className="htp-intro">
      Build math expressions on the board.<br />
      Get as close to <strong>20</strong> as possible.<br />
      Lowest score wins.
    </p>
    <div className="htp-formula-container">
      <HtpTile label="5" />
      <HtpTile label="Times" />
      <HtpTile label="4" />
      <span className="htp-tile-equals">= <em>20</em></span>
    </div>
  </>
);

const CardBoard = () => (
  <>
    <p className="htp-intro">
      The board is a 12×12 grid. Your first move must include at least one tile on
      a <strong>grey starting square</strong>, and use exactly 3 tiles.
    </p>
    <img
      src={boardScreenshot}
      alt="King of 20 game board"
      className="htp-board-screenshot"
    />
  </>
);

const CardYourTurn = () => (
  <>
    <p className="htp-intro">
      You have <strong>7 tiles</strong> on your rack. After placing,
      your tiles will be replenished from the bag.
    </p>

    <p className="htp-text">On your turn you can:</p>
    <div className="htp-turn-options">
      <div className="htp-turn-option">
        <div className="htp-turn-title">Place</div>
        <div className="htp-turn-desc">Play 1–3 tiles to form or extend a valid expression.</div>
      </div>
      <div className="htp-turn-option">
        <div className="htp-turn-title">Swap</div>
        <div className="htp-turn-desc">Return any number of tiles to the bag and draw new ones.</div>
        <div className="htp-penalty-badge">+{swapPassPenalty} pts to your score</div>
      </div>
      <div className="htp-turn-option">
        <div className="htp-turn-title">Pass</div>
        <div className="htp-turn-desc">Skip your turn.</div>
        <div className="htp-penalty-badge">+{swapPassPenalty} pts to your score</div>
      </div>
    </div>
  </>
);

const CardPlacement = () => (
  <>
    <div className="htp-placement-legend">
      <HtpTile label="Times" variant="new" />
      <span>= tile you are placing this turn</span>
    </div>

    <div className="htp-placement-examples">
      <div className="htp-placement-example valid">
        <div className="htp-example-label">Extend an existing expression</div>
        <div className="htp-tile-row">
          <HtpTile label="4" />
          <HtpTile label="Plus" />
          <HtpTile label="7" />
          <HtpTile label="Times" variant="new" />
          <HtpTile label="2" variant="new" />
        </div>
        <div className="htp-example-or"><em>or</em></div>
        <div className="htp-tile-row">
          <HtpTile label="2" variant="new" />
          <HtpTile label="Times" variant="new" />
          <HtpTile label="4" />
          <HtpTile label="Plus" />
          <HtpTile label="7" />
        </div>
        <div className="htp-example-caption">Play your tiles at either end of an expression already on the board.</div>
      </div>

      <div className="htp-placement-example valid">
        <div className="htp-example-label">A trailing operator with nothing after it is ignored</div>
        <div className="htp-tile-row">
          <HtpTile label="4" />
          <HtpTile label="Plus" />
          <HtpTile label="7" />
          <HtpTile label="Times" variant="new" />
          <HtpTile label="2" variant="new" />
          <HtpTile label="Minus" />
        </div>
        <div className="htp-example-caption">
          The <em>Minus</em> tile has no number following it, so your expression stops at "4 Plus 7 Times 2". If a number followed it, <em>Minus</em> would be part of your expression.
        </div>
      </div>
    </div>

    <div className="htp-rules-list">
      <p>Only one expression can be made per turn</p>
      <p>All tiles in one turn must be in one row or column — no gaps</p>
      <p>Diagonal expressions are not permitted</p>
      <p>No fractions — division must produce a whole number</p>
    </div>
  </>
);

const CardScoring = () => (
  <>
    <p className="htp-intro">
      How far is your result from 20? That's your score.
    </p>
    <div className="htp-rule-callout">
      ⚠️ Expressions are read <strong>left to right</strong>.
    </div>
    <p className="htp-text">
      Scores accumulate each turn — lowest total at the end of the game wins.
    </p>
    <div className="htp-formula-container">
      <HtpTile label="3" />
      <HtpTile label="Plus" />
      <HtpTile label="9" />
      <HtpTile label="Times" />
      <HtpTile label="2" />
      <span className="htp-tile-equals">= <em>24</em> → |20−24| = <strong>4 pts</strong></span>
      <p className="htp-read-aloud">3 + 9 = 12 &nbsp;→&nbsp; 12 × 2 = 24</p>
    </div>
    <div className="htp-score-callout">
      🎯 An expression that equals exactly <strong>20</strong> scores <strong>0 points</strong> — perfect!
    </div>
  </>
);

const TILE_NUMBERS = [[1,4],[2,5],[3,4],[4,6],[5,5],[6,6],[7,5],[8,6],[9,4]];
const TILE_OPERATORS = [["Plus",8],["Times",8],["Minus",8],["Over",5]];
const CardTileRef = () => (
  <>
    <p className="htp-intro">There are <strong>74 tiles</strong> total in the bag.</p>
    <p className="htp-tile-section-label">Numbers</p>
    <div className="htp-tile-grid">
      {TILE_NUMBERS.map(([n, c]) => (
        <div key={n} className="htp-tile-grid-item">
          <HtpTile label={String(n)} />
          <div className="htp-tile-count">×{c}</div>
        </div>
      ))}
    </div>
    <p className="htp-tile-section-label">Operators</p>
    <div className="htp-tile-grid">
      {TILE_OPERATORS.map(([op, c]) => (
        <div key={op} className="htp-tile-grid-item">
          <HtpTile label={op} />
          <div className="htp-tile-count">×{c}</div>
        </div>
      ))}
    </div>
  </>
);

const CardEndGame = () => (
  <>
    <p className="htp-intro">
      Once the bag is empty, the current round completes normally.<br />
      Then each player takes <strong>one final turn</strong>.<br />
      The game may end with tiles still on your rack.
    </p>
    <div className="htp-score-callout">
      🏆 The player with the <strong>lowest total score</strong> wins.
    </div>
  </>
);

const CARD_COMPONENTS = [
  { title: "Welcome to King of 20", Component: CardGoal },
  { title: "The Board",       Component: CardBoard },
  { title: "Your Turn",       Component: CardYourTurn },
  { title: "Placement Rules", Component: CardPlacement },
  { title: "Scoring",         Component: CardScoring },
  { title: "Tile Reference",  Component: CardTileRef },
  { title: "End Game",        Component: CardEndGame },
];

const HowToPlay = () => {
  const [current, setCurrent] = useState(0);
  const { title, Component } = CARD_COMPONENTS[current];
  const history = useHistory();

  const prev = () => setCurrent(i => Math.max(0, i - 1));
  // "Done" used to wrap back to the first card, stranding the reader. Send them
  // to "/" instead: the Play now page when anonymous, their games otherwise.
  const next = () => {
    if (current === CARD_COMPONENTS.length - 1) {
      history.push("/");
      return;
    }
    setCurrent(i => i + 1);
  };

  useEffect(() => {
    const onKey = e => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "Escape") {
        // A modal (login/signup) sits above this page and owns Escape first;
        // leaving while it is open would navigate behind it.
        if (document.querySelector(".modal-backdrop")) return;
        history.push("/");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // next() reads `current`, so the listener must be rebound as it changes.
  }, [current, history]);

  return (
    <div className="how-to-play">
      <h1 className="htp-visually-hidden">How to Play King of 20</h1>
      <div className="htp-card">
        <div className="htp-card-header">
          <div className="htp-header-nav">
            <button
              className={`htp-nav-btn htp-prev${current === 0 ? " htp-hidden" : ""}`}
              onClick={prev}
            >
              ← Back
            </button>
            <div className="htp-dots">
              {CARD_COMPONENTS.map((_, i) => (
                <div
                  key={i}
                  className={`htp-dot${i === current ? " active" : ""}`}
                />
              ))}
            </div>
            <button className="htp-nav-btn htp-next" onClick={next}>
              {current === CARD_COMPONENTS.length - 1 ? "Done ✓" : "Next →"}
            </button>
          </div>
          <h2 className="htp-card-title">{title}</h2>
        </div>

        <div className="htp-card-body">
          <Component />
        </div>
        <div className="htp-card-footer">
          &copy; {new Date().getFullYear()} King of 20 &middot; <a href="/privacy">Privacy Policy</a>
        </div>
      </div>
    </div>
  );
};

export default HowToPlay;
